/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { useState } from "react"
import type { To } from "react-router"
import { createSearchParams, generatePath, useNavigate } from "react-router"

import type { SelectedOrgId } from "../../features/organization/organization-types"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { UserListViewModel } from "../../features/user/user-list-viewmodel"
import type { UserList, UserRole } from "../../features/user/user-types"
import { formatRole } from "../../features/user/user-types"
import { LoggerViewModel } from "../../logger/logger-viewmodel"
import { AppRoutes } from "../../routes/routes"
import { useViewModel } from "../app/app-hooks"
import { PaginationControls } from "../components/pagination-controls"
import { TextFilter } from "../ui-widgets/filter/input-filter"
import { SelectFilter } from "../ui-widgets/filter/select-filter"
import { AllUsersSelectOrgView } from "./all-users-select-org-view"
import { RoleMultiSelector } from "./role-filter-view"
import { UserCreateModal } from "./user-create-modal"
import { UserInviteModal } from "./user-invite-modal"

import "./all-users-view.css"

export const AllUsersView = observer(() => {
    const listVM = useViewModel(UserListViewModel)
    const orgVM = useViewModel(OrganizationViewModel)
    const loggerVM = useViewModel(LoggerViewModel)
    const navigate = useNavigate()

    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showInviteModal, setShowInviteModal] = useState(false)

    // Filter updates:
    const handleSearchChange = (text: string) => listVM.setSearchFilter(text)
    const handleStatusChange = (status: boolean | null) => listVM.setStatusFilter(status)
    const handleRolesChange = (roles: string[]) => {
        // Filter only valid roles
        const validRoles = roles.filter(listVM.isValidUserRole) as UserRole[]
        listVM.setRolesFilter(validRoles)
    }
    const handleExternalChange = (external: boolean | null) => listVM.setExternalFilter(external)
    const handleOrgSelected = async (orgId: SelectedOrgId | null) => {
        // null is an implementation error in Select and "none" is no selection -> do nothing
        if (orgId === null || orgId === "none") {
            return
        } else {
            await listVM.getUsers(orgId)
            orgVM.setSelectedOrgId(orgId)
        }
    }

    const handleReset = () => {
        orgVM.setSelectedOrgId("none")
        listVM.resetFilters()
    }

    const handleCreateUser = () => {
        if (!orgVM.selectedOrgId || orgVM.selectedOrgId === "none") {
            loggerVM.logErrorMessage("Please select a specific organization first.")
        } else {
            setShowCreateModal(true)
        }
    }

    const handleInviteUser = () => {
        if (!orgVM.selectedOrgId || orgVM.selectedOrgId === "none") {
            loggerVM.logErrorMessage("Please select a specific organization first.")
        } else {
            setShowInviteModal(true)
        }
    }

    const organizationOptions = [...orgVM.organizations.map((org) => ({ label: `${org.name}`, value: org.id }))]

    const userRowClicked = (id?: number, orgId?: number) => {
        // if user is external, than we should use orgId of current logged in admin
        let safeOrgId: SelectedOrgId = "none"
        if (!orgId) {
            const selectedOrgId = orgVM.selectedOrgId
            if (isOrganizationSelected(selectedOrgId)) {
                safeOrgId = selectedOrgId
            } else {
                loggerVM.logErrorMessage("Before continue, select organization.")
                navigate(AppRoutes.AllUsers)
                return
            }
        } else {
            safeOrgId = orgId
        }

        if (id) {
            navigate(userDetailPath(id, safeOrgId))
        }
    }

    // Render organization selector first (if no org selected and user has more than 1 org)
    // If there's only 1 org, route-effect is already downloading users for it
    if (orgVM.organizationIds.length > 1 && orgVM.selectedOrgId === "none") {
        return (
            <AllUsersSelectOrgView
                selectedOrgId={orgVM.selectedOrgId}
                organizationOptions={organizationOptions}
                onOrganizationSelected={handleOrgSelected}
            />
        )
    }

    return (
        <div className="w-content all-users-view col-xxl-10">
            <div className="flex justify-between items-start flex-wrap gap-legacy mb-2">
                <h3 className="title mb-0">
                    Users <span> of {orgVM.selectedOrgName}</span>
                    {orgVM.hasMultipleOrganizations && (
                        <span className="change-org" onClick={handleReset}>
                            (<span className="underline">change org.</span>)
                        </span>
                    )}
                </h3>

                {/* Right side: org info + buttons side by side */}
                <div className="flex gap-2">
                    <button className="btn w-button" onClick={handleCreateUser}>
                        <img src="/assets/ic-person-plus-fill-light.svg" alt="Create user" className={"me-2"} />
                        <span>Create User</span>
                    </button>
                    <button type="button" className="btn w-button" onClick={handleInviteUser}>
                        <img
                            src="/assets/ic-envelope-paper-fill-light.svg"
                            alt="Invite external user"
                            className={"me-2"}
                        />
                        <span>Invite External User</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="row items-end my-6">
                <div className="col flex flex-wrap gap-legacy">
                    <TextFilter
                        label="Search: "
                        value={listVM.filter.search}
                        placeholder="Name or username"
                        onChange={handleSearchChange}
                    />
                    <SelectFilter
                        label="Status: "
                        options={statusOptions}
                        value={listVM.filter.status}
                        onChange={handleStatusChange}
                        noSelectionLabel="All"
                    />
                    <SelectFilter
                        label="External: "
                        options={externalOptions}
                        value={listVM.filter.external}
                        onChange={handleExternalChange}
                        noSelectionLabel="All"
                    />
                    <RoleMultiSelector selectedRoles={listVM.filter.roles} onChange={handleRolesChange} />
                </div>

                {/* Top Pagination */}
                {!listVM.isLoadingUsers && listVM.totalPages > 1 && (
                    <div className="mb-1">
                        <PaginationControls
                            currentPage={listVM.currentPage}
                            totalPages={listVM.totalPages}
                            onPageChange={(page) => listVM.setPage(page)}
                            className="mt-6"
                            aria-label="pagination, above the users"
                        />
                    </div>
                )}
            </div>

            {/* Main table */}
            <div className="card shadow-legacy-sm overflow-hidden mb-1">
                <div className="table-responsive">
                    <table className="table table-hover m-0 table-extra-spacing custom-row-height align-middle">
                        <thead className="table-light">
                            <tr>
                                <th className="whitespace-nowrap">User ID</th>
                                <th>Name</th>
                                <th>Username/Email</th>
                                <th className="whitespace-nowrap">Org ID</th>
                                <th className="text-center">Enabled</th>
                                <th className="text-center">External</th>
                                <th>Roles</th>
                            </tr>
                        </thead>
                        <tbody>
                            {listVM.isLoadingUsers ? (
                                renderPlaceholder()
                            ) : listVM.pagedUsers.length > 0 ? (
                                listVM.pagedUsers.map((user) => renderUser(user, userRowClicked))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="text-legacy-muted text-center py-legacy">
                                        {listVM.hasUsers ? "No users found matching filters." : "No users found."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bottom Pagination */}
            {!listVM.isLoadingUsers && listVM.totalPages > 1 && (
                <PaginationControls
                    currentPage={listVM.currentPage}
                    totalPages={listVM.totalPages}
                    onPageChange={(page) => listVM.setPage(page)}
                    className="mt-6"
                    aria-label="pagination, below the users"
                />
            )}

            {/* User Create Modal */}
            {showCreateModal && isOrganizationSelected(orgVM.selectedOrgId) && (
                <UserCreateModal
                    orgId={orgVM.selectedOrgId}
                    orgName={orgVM.selectedOrgName}
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={(userId) => {
                        setShowCreateModal(false)
                        navigate(userDetailPath(userId, orgVM.selectedOrgId))
                    }}
                />
            )}

            {/* User Invite Modal */}
            {showInviteModal && isOrganizationSelected(orgVM.selectedOrgId) && (
                <UserInviteModal
                    orgId={orgVM.selectedOrgId}
                    orgName={orgVM.selectedOrgName}
                    onClose={() => setShowInviteModal(false)}
                    onSuccess={() => {
                        setShowInviteModal(false)
                        // Refresh the user list
                        if (typeof orgVM.selectedOrgId === "number") {
                            void listVM.getUsers(orgVM.selectedOrgId)
                        }
                    }}
                />
            )}
        </div>
    )
})

function renderUser(user: UserList, onClick: (id?: number, orgId?: number) => void) {
    return (
        <tr key={user.id} onClick={() => onClick(user.id, user.organizationId)} className="cursor-pointer">
            <td className="whitespace-nowrap">#{user.id}</td>
            <td className="whitespace-nowrap" scope="row">
                {user.name}
            </td>
            <td className="whitespace-nowrap" scope="row">
                {user.username}
            </td>
            <td className="whitespace-nowrap text-center" scope="row">
                #{user.organizationId}
            </td>
            <td className="whitespace-nowrap text-center" scope="row">
                {user.enabled !== undefined && (
                    <>
                        <span
                            className={`legend-circle ${user.enabled ? "bg-legacy-success" : "bg-legacy-danger"}`}
                        ></span>
                        {user.enabled ? "Enabled" : "Disabled"}
                    </>
                )}
            </td>

            <td className="whitespace-nowrap text-center" scope="row">
                {user.external !== undefined && (
                    <>
                        <span
                            className={`legend-circle ${user.external ? "bg-legacy-warning" : "bg-legacy-success"}`}
                        ></span>
                        {user.external ? "External" : "Internal"}
                    </>
                )}
            </td>
            <td scope="row">
                {user.roles && user.roles.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                            <span key={role} className="badge bg-legacy-primary">
                                {formatRole(role)}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="text-legacy-muted">None</span>
                )}
            </td>
        </tr>
    )
}

function renderPlaceholder(itemsCount: number = 10) {
    return [...Array(itemsCount)]
        .map((_, i) => `placeholder-${i}`)
        .map((key) => (
            <tr key={key} className="placeholder-glow">
                <td>
                    <span className="placeholder col-6 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-6 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-3 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-4 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-2 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-2 bg-legacy-secondary"></span>
                </td>
                <td>
                    <span className="placeholder col-4 bg-legacy-secondary"></span>
                </td>
            </tr>
        ))
}

// Filters setup
const statusOptions = [
    { label: "Enabled", value: true },
    { label: "Disabled", value: false }
]

const externalOptions = [
    { label: "External", value: true },
    { label: "Internal", value: false }
]

/**
 * Builds the user detail location, including the organization the user belongs to.
 *
 * The detail page uses `orgId` to determine how the user relates to the current
 * organization, so it is passed in the URL rather than looked up again.
 *
 * @param userId - ID of the user to open.
 * @param orgId - Organization the user belongs to, or `"none"` when no organization is selected.
 * @returns A location with the user ID in the path and the organization ID in the query.
 */
function userDetailPath(userId: number, orgId: SelectedOrgId): To {
    return {
        pathname: generatePath(AppRoutes.UserDetail, { id: `${userId}` }),
        search: `?${createSearchParams({ orgId: `${orgId}` })}`
    }
}

/**
 * Narrows `SelectedOrgId` to a selected organization ID.
 *
 * `"none"` represents no selection, so this is a type guard rather than a
 * conversion. The predicate narrows `orgId` to `number` at the call site.
 *
 * @param orgId - Current organization selection.
 * @returns `true` when an organization is selected.
 */
function isOrganizationSelected(orgId: SelectedOrgId): orgId is number {
    return orgId !== "none"
}
